import {
  Card,
  CardContent,
  Typography,
  Stack,
  Box,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";

import { MY_PERF_SCROLL_LIST_PB } from "../../Dashboard/layouts";

const FONT = '"Helvetica Neue", Arial, sans-serif';
const CURRENT_MONTH_BLUE = "#1F6AA5";

function institutionSpecialtyLine(institutionName, npiSpecialty) {
  const inst = (institutionName ?? "").toString().trim();
  const spec = (npiSpecialty ?? "").toString().trim();
  if (inst && spec) return `${inst} - ${spec}`;
  if (inst) return inst;
  if (spec) return spec;
  return "";
}

const headerCellSx = {
  border: "none",
  fontFamily: FONT,
  fontSize: "12px",
  fontWeight: 700,
  color: "#454545",
  backgroundColor: "#EEF0F1",
  py: 1,
};

const bodyCellBaseSx = {
  border: "none",
  borderBottom: "1px solid #E0E0E0",
  fontFamily: FONT,
  fontSize: "14px",
  fontWeight: 400,
  color: "#454545",
  backgroundColor: "#FFFFFF",
  py: 1.75,
};

/** Name stacks two lines; metrics sit vertically centered to that block. */
const nameBodyCellSx = {
  ...bodyCellBaseSx,
  verticalAlign: "top",
};

const metricBodyCellSx = {
  ...bodyCellBaseSx,
  verticalAlign: "middle",
};

function TableSkeleton() {
  return (
    <TableContainer
      sx={{
        flex: "1 1 0",
        minHeight: 0,
        maxHeight: 400,
        overflowY: "auto",
        overflowX: "hidden",
        pr: 0.5,
        width: "100%",
        maxWidth: "100%",
      }}
    >
      <Stack spacing={1.2} sx={{ py: 1, pb: MY_PERF_SCROLL_LIST_PB }}>
        {[...Array(5)].map((_, idx) => (
          <Skeleton key={idx} variant="rounded" height={24} />
        ))}
      </Stack>
    </TableContainer>
  );
}

export default function TopYutrepiaWritersCard({
  title = "Top Yutrepia Writers",
  rows = [],
  isLoading = false,
  onExpand,
}) {
  const hasRows = Array.isArray(rows) && rows.length > 0;

  return (
    <Card
      variant="outlined"
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        borderRadius: "8px",
        border: "0.5px solid #F0F0F0",
        boxShadow: "1px 4px 4px 0px #00000008",
      }}
    >
      <CardContent
        sx={{
          p: 3,
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box sx={{ flexShrink: 0 }}>
          <Stack
            direction="row"
            alignItems="flex-start"
            justifyContent="space-between"
            spacing={1}
          >
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                fontSize: "14px",
                lineHeight: 1.2,
                color: "#000",
              }}
            >
              {title}
            </Typography>
          </Stack>
        </Box>

        {isLoading ? (
          <TableSkeleton />
        ) : (
          <TableContainer
            sx={{
              flex: "1 1 0",
              minHeight: 0,
              maxHeight: 400,
              overflowY: "auto",
              overflowX: "hidden",
              pr: 0.5,
              width: "100%",
              maxWidth: "100%",
            }}
          >
            {!hasRows ? (
              <Box
                sx={{
                  minHeight: 120,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography sx={{ color: "#718096", fontSize: 14 }}>

                  No data found
                </Typography>
              </Box>
            ) : (
              <Box sx={{ pb: MY_PERF_SCROLL_LIST_PB }}>
                <Table
                  stickyHeader
                  size="small"
                  sx={{
                    tableLayout: "fixed",
                    width: "100%",
                  }}
                >
                  <TableHead>
                    <TableRow >
                      <TableCell sx={{ ...headerCellSx, textAlign: "left", width: "46%" }}>
                        Name
                      </TableCell>
                      <TableCell sx={{ ...headerCellSx, textAlign: "right", width: "27%" }}>
                        Current Month
                      </TableCell>
                      <TableCell sx={{ ...headerCellSx, textAlign: "right", width: "27%" }}>
                        Prior Month
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row, idx) => {
                      const institutionSpecialty = institutionSpecialtyLine(
                        row.institutionName,
                        row.npiSpecialty,
                      );
                      return (
                        <TableRow key={row.id ?? `tyw-${idx}`}>
                          <TableCell
                            sx={{
                              ...nameBodyCellSx,
                              textAlign: "left",
                              wordBreak: "break-word",
                              overflowWrap: "break-word",
                            }}
                          >
                            <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                              <Typography
                                sx={{
                                  fontSize: 13,
                                  fontWeight: 600,
                                  color: "#2D2D2D",
                                  lineHeight: 1.2,
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {row.name || "—"}
                              </Typography>
                              {institutionSpecialty ? (
                                <Typography
                                  sx={{
                                    fontSize: 12,
                                    color: "#718096",
                                    lineHeight: 1.2,
                                    wordBreak: "break-word",
                                    overflowWrap: "break-word",
                                  }}
                                >
                                  {institutionSpecialty}
                                </Typography>
                              ) : null}
                            </Stack>
                          </TableCell>
                          <TableCell
                            sx={{
                              ...metricBodyCellSx,
                              textAlign: "right",
                              fontWeight: 700,
                              color: CURRENT_MONTH_BLUE,
                            }}
                          >
                            {row.currentMonth ?? "—"}
                          </TableCell>
                          <TableCell sx={{ ...metricBodyCellSx, textAlign: "right" }}>
                            {row.priorMonth ?? "—"}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            )}
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
}
